/**
 * Ads360 brand mark — a growth "orbit + arrow" ring with an ascending bar chart
 * and a play triangle, in the blue→purple brand gradient. Self-contained SVG so
 * it scales crisply at any size. To use an exact raster logo instead, drop the
 * file at `public/logo.png` and swap the usages for `<img src="/logo.png" .../>`.
 */
import type { ReactElement } from 'react';

export interface LogoProps {
  /** Tailwind sizing (default h-8 w-8). */
  className?: string;
}

let uid = 0;

/** The Ads360 logo mark. */
export default function Logo({ className = 'h-8 w-8' }: LogoProps): ReactElement {
  // Unique gradient id so multiple instances on a page don't collide.
  const gid = `ads360-grad-${(uid += 1)}`;
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Ads360"
    >
      <defs>
        <linearGradient id={gid} x1="6" y1="8" x2="42" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2563EB" />
          <stop offset="1" stopColor="#7C3AED" />
        </linearGradient>
      </defs>
      {/* Growth orbit — an open ring that sweeps up to the arrow. */}
      <path
        d="M36 10.5A18 18 0 1 0 41.5 24"
        stroke={`url(#${gid})`}
        strokeWidth="4"
        strokeLinecap="round"
      />
      {/* Arrowhead completing the orbit (upward growth). */}
      <path
        d="M33.5 7l8 2.2-2.2 8"
        stroke={`url(#${gid})`}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Ascending bar chart. */}
      <rect x="14" y="27.5" width="4.2" height="7.5" rx="1.3" fill={`url(#${gid})`} />
      <rect x="20.5" y="23" width="4.2" height="12" rx="1.3" fill={`url(#${gid})`} />
      {/* Play triangle (media / automation). */}
      <path d="M28.5 17.5l9 5.8-9 5.8z" fill={`url(#${gid})`} />
    </svg>
  );
}
